import type { DxfMappingRole } from '@frontend/api';
import {
  IconButton,
  Tooltip,
  Text,
  IconButtonGroup,
} from '@frontend/legacy-components';

import {
  MAPPING_ROLE_DEFINITIONS,
  activeRoleLabelColor,
} from '../session/roles';

export type DxfRoleActionsProps = {
  /**
   * Roles every selected entity already carries — highlighted so the user can
   * see what they have before they change it. Empty when the selection is empty
   * or carries no shared roles (e.g. a mixed selection).
   */
  activeRoles: ReadonlySet<DxfMappingRole>;
  onPick: (role: DxfMappingRole) => void;
  /** All roles are inert while the file is locked. */
  isDisabled?: boolean;
};

/**
 * The Cut / Etch / Bend / Ignore group the selection flyout picks from. Renders
 * from `MAPPING_ROLE_DEFINITIONS`, so a role added to the registry appears here
 * without this file being touched.
 *
 * Pure and controlled: it knows nothing about the session, which is what lets
 * the caller decide what a pick means (toggle, assign, close, deselect, and so on).
 */
export const DxfRoleActions = ({
  activeRoles,
  onPick,
  isDisabled = false,
}: DxfRoleActionsProps) => (
  <IconButtonGroup size="md" borderRadius="md">
    {MAPPING_ROLE_DEFINITIONS.map(({ role, label, color }) => {
      const isActive = activeRoles.has(role);

      return (
        <Tooltip
          key={role}
          placement="top"
          label={`Assign ${label.toLowerCase()}`}
        >
          <IconButton
            aria-label={`Assign ${label}`}
            aria-pressed={isActive}
            isDisabled={isDisabled}
            w="auto"
            onClick={() => onPick(role)}
            px={1}
            m={0}
            borderRadius="3px"
            // The role's own color carries the meaning; the variant carries the
            // state. Tinted only when active, so the group reads as a set of
            // choices rather than four competing colours. Ignore's chip fill is
            // light, so it gets a strong foreground instead of the usual
            // white-on-color — see `activeRoleLabelColor`.
            {...(isActive && {
              bg: color,
              color: activeRoleLabelColor(role),
              _hover: { bg: color },
            })}
          >
            <Text color="inherit" fontWeight="heavy">
              {label}
            </Text>
          </IconButton>
        </Tooltip>
      );
    })}
  </IconButtonGroup>
);
DxfRoleActions.displayName = 'DxfRoleActions';
